import React from 'react';
import { Link } from 'react-router-dom';
import { PhoneIcon, MailIcon, CheckCircleIcon, ShieldIcon } from 'lucide-react';
import bouncedadsLogo from '../images/bouncedads2.png';
import backgroundImage from '../images/background.png';
import bounceDads from '../images/bd.png';
import { getContent } from '../config/content';
import { theme } from '../config/theme';

const Home: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div 
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }} 
        className="text-white relative"
      >
        {/* Optional overlay for better text readability */}
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
          <div className="text-center">
            <div className="mb-6">
              <img 
                src={bouncedadsLogo} 
                alt="Bounce Dads Logo" 
                className="h-20 w-20 md:h-32 md:w-32 object-contain mx-auto mb-4"
              />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {getContent('home.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl mb-8" style={{ color: theme.colors.primary[100] }}>
              {getContent('home.hero.subtitle')}
            </p>
            <div className="space-x-4">
              <Link
                to="/rentals"
                style={{ backgroundColor: 'white', color: theme.colors.primary[800] }}
                className="px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-colors"
              >
                {getContent('home.hero.cta.primary')}
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white transition-colors"
                style={{ '--hover-color': theme.colors.primary[800] } as React.CSSProperties}
              >
                {getContent('home.hero.cta.secondary')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {getContent('home.features.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {getContent('home.features.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {getContent('home.features.items').map((feature: any, index: number) => (
              <div key={index} className="text-center">
                <div className="rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#dbeafe' }}>
                  {index === 0 ? (
                    <CheckCircleIcon className="h-8 w-8" style={{ color: '#1e40af' }} />
                  ) : index === 1 ? (
                    <PhoneIcon className="h-8 w-8" style={{ color: '#1e40af' }} />
                  ) : index === 2 ? (
                    <MailIcon className="h-8 w-8" style={{ color: '#1e40af' }} />
                  ) : (
                    <ShieldIcon className="h-8 w-8" style={{ color: '#1e40af' }} />
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* About Us Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {getContent('home.aboutUs.title')}
              </h2>
              <div className="prose prose-lg text-gray-600">
                {getContent('home.aboutUs.content').split('\n\n').map((paragraph: string, index: number) => (
                  <p key={index} className="mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="text-center">
              <div className="rounded-lg overflow-hidden shadow-lg inline-block bg-white p-8">
                <img 
                  src={bounceDads} 
                  alt={getContent('home.aboutUs.teamImageAlt')}
                  className="w-64 h-64 object-contain mx-auto"
                  onError={(e) => {
                    // Fallback to logo if team photo not found
                    e.currentTarget.src = bouncedadsLogo;
                    e.currentTarget.alt = 'Bounce Dads Logo';
                  }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-4 italic">
                The Bounce Dads Team
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {getContent('home.cta.title')}
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            {getContent('home.cta.subtitle')}
          </p>
          <Link
            to="/rentals"
            style={{ backgroundColor: theme.colors.primary[800] }}
            className="text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-colors"
          >
            {getContent('home.cta.button')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;
